import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconCamera, IconPhoto, IconVideo } from '@tabler/icons-react';
import { Menu, Button, Modal, Stack, Group, Text, Paper } from '@mantine/core';

type ImageSourcePickerProps = {
  disabled?: boolean;
  multiple?: boolean;
  allowImages?: boolean;
  onSelect: (files: File[]) => void | Promise<void>;
  onSelectVideo?: (files: File[]) => void | Promise<void>;
  renderTrigger: (controls: { open: () => void; openState: boolean }) => React.ReactNode;
};

export function ImageSourcePicker({
  disabled = false,
  multiple = false,
  allowImages = true,
  onSelect,
  onSelectVideo,
  renderTrigger,
}: ImageSourcePickerProps) {
  const [menuOpened, setMenuOpened] = useState(false);
  const [cameraOpened, setCameraOpened] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const cameraInputId = useId();
  const galleryInputId = useId();

  useEffect(() => () => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
  }, []);

  const attachCameraVideo = useCallback((video: HTMLVideoElement | null) => {
    cameraVideoRef.current = video;
    if (!video) {
      return;
    }

    const stream = cameraStreamRef.current;
    if (!stream) {
      return;
    }

    video.srcObject = stream;
    void video.play().catch(() => undefined);
  }, []);

  const emitFiles = async (files: FileList | null) => {
    const nextFiles = Array.from(files ?? []);
    if (nextFiles.length === 0) return;
    stopCameraStream();
    setCameraOpened(false);
    setMenuOpened(false);
    await onSelect(nextFiles);
  };

  const emitVideoFiles = async (files: FileList | null) => {
    const nextFiles = Array.from(files ?? []);
    if (nextFiles.length === 0 || !onSelectVideo) return;
    setMenuOpened(false);
    await onSelectVideo(nextFiles);
  };

  const openGallery = () => galleryInputRef.current?.click();

  const stopCameraStream = () => {
    setCameraReady(false);
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      setCameraBusy(true);
      setCameraReady(false);
      setCameraError(null);
      stopCameraStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraOpened(true);
    } catch (error) {
      console.warn('camera unavailable, fallback to file input', error);
      setCameraError('暂时无法直接打开摄像头，已切换为文件选择。');
      cameraInputRef.current?.click();
    } finally {
      setCameraBusy(false);
    }
  };

  const capturePhoto = async () => {
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    if (
      !video
      || !canvas
      || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
      || video.videoWidth === 0
      || video.videoHeight === 0
    ) {
      setCameraReady(false);
      setCameraError('摄像头画面尚未就绪，请稍候重试。');
      return;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
    if (!blob) {
      setCameraError('拍照失败，请重试。');
      return;
    }
    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
    stopCameraStream();
    setCameraOpened(false);
    setMenuOpened(false);
    setCameraError(null);
    await onSelect([file]);
  };

  return (
    <>
      <input
        id={galleryInputId}
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={async (event) => {
          await emitFiles(event.target.files);
          event.target.value = '';
        }}
      />
      {onSelectVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          style={{ display: 'none' }}
          onChange={async (event) => {
            await emitVideoFiles(event.target.files);
            event.target.value = '';
          }}
        />
      )}
      <input
        id={cameraInputId}
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={async (event) => {
          await emitFiles(event.target.files);
          event.target.value = '';
        }}
      />

      <Menu
        opened={menuOpened}
        onChange={setMenuOpened}
        disabled={disabled}
        position="top-end"
        offset={12}
        radius="md"
        width="auto"
        shadow="xl"
      >
        <Menu.Target>
          {renderTrigger({
            open: () => setMenuOpened((v) => !v),
            openState: menuOpened,
          })}
        </Menu.Target>

        <Menu.Dropdown p="xs">
          {allowImages && (
            <>
              <Menu.Item
                leftSection={<IconCamera size={18} />}
                onClick={() => void openCamera()}
                disabled={cameraBusy}
              >
                <Text size="sm" fw={600}>拍照</Text>
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPhoto size={18} />}
                onClick={openGallery}
              >
                <Text size="sm" fw={600}>选择图片</Text>
              </Menu.Item>
            </>
          )}
          {onSelectVideo && (
            <Menu.Item
              leftSection={<IconVideo size={18} />}
              onClick={() => videoInputRef.current?.click()}
            >
              <Text size="sm" fw={600}>选择视频</Text>
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={cameraOpened}
        onClose={() => {
          stopCameraStream();
          setCameraOpened(false);
        }}
        title="拍摄照片"
        size="md"
        centered
        radius="lg"
      >
        <Stack gap="md">
          <Paper radius="md" bg="gray.9" style={{ overflow: 'hidden', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video
              ref={attachCameraVideo}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              autoPlay
              playsInline
              muted
              onLoadedData={(event) => {
                const video = event.currentTarget;
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                  setCameraReady(true);
                  setCameraError(null);
                }
              }}
            />
            <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />
          </Paper>

          {cameraError && (
            <Text c="red" size="sm" ta="center">
              {cameraError}
            </Text>
          )}

          <Group justify="center" gap="xl" pb="md">
            <Button variant="default" radius="xl" size="md" onClick={() => {
              stopCameraStream();
              setCameraOpened(false);
            }}>
              取消
            </Button>
            <Button
              radius="xl"
              size="md"
              leftSection={<IconCamera size={20} />}
              onClick={() => void capturePhoto()}
              disabled={!cameraReady}
              loading={!cameraReady}
            >
              拍照
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
